import { RefreshCw } from 'lucide-react'

/**
 * LoadingButton Component
 * Reusable button component with built-in loading indicator
 * Prevents multiple submissions and provides visual feedback
 */
export const LoadingButton = ({
  isLoading = false,
  disabled = false,
  onClick,
  children,
  className = '',
  loadingText = null,
  spinnerPosition = 'left', // 'left' or 'right'
  type = 'button',
  title = '',
  variant = 'primary', // 'primary', 'danger', 'success', 'secondary', 'warning'
  size = 'md' // 'sm', 'md', 'lg'
}) => {
  const baseClasses = 'font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap'
  
  const variantClasses = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700',
    danger: 'bg-red-600 text-white hover:bg-red-700',
    success: 'bg-green-600 text-white hover:bg-green-700',
    secondary: 'bg-gray-600 text-white hover:bg-gray-700',
    warning: 'bg-amber-600 text-white hover:bg-amber-700'
  }

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  }

  const spinnerSize = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  }

  const combinedClassName = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} rounded-lg ${className}`
  
  const isDisabled = isLoading || disabled

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      className={combinedClassName}
      title={title}
    >
      {isLoading && spinnerPosition === 'left' && (
        <RefreshCw className={`${spinnerSize[size]} animate-spin flex-shrink-0`} />
      )}
      <span>{isLoading && loadingText ? loadingText : children}</span>
      {isLoading && spinnerPosition === 'right' && (
        <RefreshCw className={`${spinnerSize[size]} animate-spin flex-shrink-0`} />
      )}
    </button>
  )
}

/**
 * IconButton Component
 * Button with only an icon and loading indicator
 */
export const IconButton = ({
  icon: Icon,
  isLoading = false,
  disabled = false,
  onClick,
  className = '',
  title = '',
  variant = 'primary',
  size = 'md'
}) => {
  const variantClasses = {
    primary: 'text-blue-600 hover:bg-blue-50',
    danger: 'text-red-600 hover:bg-red-50',
    success: 'text-green-600 hover:bg-green-50',
    secondary: 'text-gray-600 hover:bg-gray-50',
    warning: 'text-amber-600 hover:bg-amber-50'
  }

  const sizeClasses = {
    sm: 'p-1',
    md: 'p-2',
    lg: 'p-3'
  }

  const iconSize = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  }

  const combinedClassName = `${variantClasses[variant]} ${sizeClasses[size]} rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isLoading || disabled}
      className={combinedClassName}
      title={title}
    >
      {isLoading ? (
        <RefreshCw className={`${iconSize[size]} animate-spin`} />
      ) : (
        <Icon className={iconSize[size]} />
      )}
    </button>
  )
}

export default LoadingButton
